import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { AccessControlService } from "app/access-control/access-control-service";
import {
  Events,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
} from "ionic-angular";
import {
  LoginResponse,
  PatientLogDtoLevelEnum,
  PatientResponseDto,
} from "models/scriptSure";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Observable, Subscription } from "rxjs";

export type DestinationPageType =
  | "dashboard"
  | "messages"
  | "drug-list"
  | "patient";

@Component({
  selector: "eprescribe-iframe",
  templateUrl: "eprescribe.component.html",
})
export class EPrescribeIFrame implements OnInit, OnDestroy {
  @Input() set destinationPage(destinationPage: string) {
    this._destinationPage = destinationPage;
    this.loadIFrame();
  }
  @Output() errorCloseButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();

  _destinationPage: string = "patient";
  pageType: string = "";
  show: boolean = false;
  iframeToken: LoginResponse;
  iframeUrl: string = "";
  errorHeader: string = "";
  listOfErrors: Array<string> = [];
  spinnerCounter = 0;
  spinner = null;
  loading = false;

  private navBarSubscription: Subscription;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public modalCtrl: ModalController,
    public loadingCtrl: LoadingController,
    public accessCntrl: AccessControlService,
    public referralsService: ReferralsServiceProvider,
    state: StateManagementService,
    public stateData: StateManagementService,
    public medicationDataService: MedicationDataService,
    public navBarService: NavBarServiceProvider,
    public events: Events,
    private config: ConfigurationService
  ) {
    this.referralsService.ACTIVE_TAB_NAME = "eprescribe";
    if (this.referralsService.PATIENT_STATUS) {
      this.pageType = this.referralsService.PATIENT_STATUS;
    }
  }
  ngOnDestroy(): void {
    if (this.navBarSubscription) {
      this.navBarSubscription.unsubscribe();
    }
    // loadIFrame runs on the "reloadNavBar" event; unsubscribe the topic so the
    // handler does not outlive the component (CO-3070 leak fix).
    this.events.unsubscribe("reloadNavBar");
  }

  ngOnInit(): void {
    this.loadIFrame();
    this.events.subscribe("reloadNavBar", () => {
      this.loadIFrame();
    });
  }

  loadIFrame() {
    // loadIFrame is called from the destinationPage setter, ngOnInit and the
    // "reloadNavBar" event; release the previous getScriptSurePatient()
    // subscription before opening a new one so they do not accumulate
    // (CO-3070 leak fix).
    if (this.navBarSubscription) {
      this.navBarSubscription.unsubscribe();
    }
    // Handle patient
    this.navBarSubscription = this.navBarService
      .getScriptSurePatient()
      .subscribe((scriptSurePatient) => {
        if (
          ["drug-list", "patient"].includes(this._destinationPage) &&
          !scriptSurePatient
        ) {
          return;
        } else {
          if (this.loading) {
            return;
          }
          this.loading = true;
          this.errorHeader = "";
          this.listOfErrors = [];

          Observable.zip(
            this.medicationDataService.getLogin(),
            this.medicationDataService.getOrganization()
          )
            .finally(() => {
              this.loading = false;
            })
            .subscribe(
              ([responseData, organization]) => {
                const { sessionToken } = responseData;
                if (["dashboard", "messages"].includes(this._destinationPage)) {
                  try {
                    this.iframeUrl = this.getIFrameURL(
                      organization.appBaseUrl,
                      this._destinationPage,
                      sessionToken,
                      organization.appId,
                      scriptSurePatient
                    );
                  } catch (error) {
                    this.errorHeader = error.message; // No URL in config
                  }
                } else {
                  const mostRecentLog = scriptSurePatient.logs[0];
                  const mostRecentLogIsError = mostRecentLog
                    ? mostRecentLog.level === PatientLogDtoLevelEnum.ERROR
                    : false;
                  if (scriptSurePatient.patient) {
                    this.iframeUrl = this.getIFrameURL(
                      organization.appBaseUrl,
                      this._destinationPage,
                      sessionToken,
                      organization.appId,
                      scriptSurePatient
                    );
                  } else if (mostRecentLogIsError) {
                    this.errorHeader =
                      "Patient can not be found. There was a problem sending the patient's demographics:";
                    if (
                      mostRecentLog.errorList &&
                      mostRecentLog.errorList.length > 0
                    ) {
                      this.listOfErrors.push(
                        ...mostRecentLog.errorList.map((error) => error.message)
                      );
                    } else {
                      this.listOfErrors.push(mostRecentLog.message);
                    }
                  } else {
                    this.errorHeader =
                      "Please note: This patient has not yet been sent to the e-Prescribe system. Please allow a few minutes for processing. If the patient does not appear in ScriptSure, contact your Administrator or Support Team for assistance.";
                  }
                }
              },
              (error) => {
                this.errorHeader = "Provider not in ScriptSure.";
                this.listOfErrors.push(
                  "You are not an e-Prescribe provider. Please contact your Administrator to request an email invitation."
                );
              }
            );
        }
      });
  }

  getIFrameURL(
    appBaseUrl: string,
    destination: string,
    sessionToken: string,
    appId: number,
    scriptSurePatient?: PatientResponseDto
  ): string {
    // Set URL Components
    if (!this.config.getConfig().scriptSureEnabled) {
      throw new Error("ScriptSure not enabled for this environment");
    }
    const queryString = `sessiontoken=${sessionToken}&darkmode=off&appId=${appId}`;
    const scriptSurePatientIdString = this.getScriptSurePatientIdString(
      destination,
      scriptSurePatient
    );

    // Construct URL
    switch (destination) {
      case "dashboard":
        return `${appBaseUrl}/?${queryString}`;
      case "messages":
        return `${appBaseUrl}/widgets/message/${scriptSurePatientIdString}?${queryString}`;
      case "drug-list":
        return `${appBaseUrl}/widgets/drug-list/${scriptSurePatientIdString}?${queryString}`;
      case "patient":
        return `${appBaseUrl}/chart/${scriptSurePatientIdString}/prescriptions?${queryString}`;
      default:
        return `${appBaseUrl}/?${queryString}`;
    }
  }

  getScriptSurePatientIdString(
    destination: string,
    scriptSurePatient?: PatientResponseDto
  ): string {
    if (
      ["drug-list", "patient"].includes(destination) &&
      scriptSurePatient &&
      scriptSurePatient.patient
    ) {
      return `${scriptSurePatient.patient.scriptSurePatientId}`;
    }

    return "0";
  }

  get showError(): boolean {
    return Boolean(this.errorHeader) || this.listOfErrors.length > 0;
  }

  get showInfo(): boolean {
    return this.showError;
  }

  /**
   * CO-3356 — whether this host sits inside the PATIENT chart chrome.
   *
   * The width-offset classes on <main> exist to leave room for the patient
   * chart's brief bar / side rail. They were applied on `pageType` alone
   * (`!== 'referral'` vs `=== 'referral'`), which describes the referral stage —
   * NOT which host is rendering. So the provider dashboard, which has no
   * patient and therefore no brief bar, still reserved the gutter:
   * `patient-details-eprescribe-content-with-briefbar` is `calc(100% - 235px)`,
   * measured as a 235px dead strip (18% of a 1280px viewport) with the
   * ScriptSure app squeezed into what remained. Every value of `pageType`
   * produced a gutter — the 'referral' branch reserves 111px instead.
   *
   * The offsets belong to the patient hosts ("patient"/"drug-list"), never to
   * the provider-level ones ("dashboard"/"messages").
   */
  get hasPatientChrome(): boolean {
    return !["dashboard", "messages"].includes(this._destinationPage);
  }

  refresh() {
    const pateintChart = this.navBarService.getPatientChart();
    this.navBarService.setScriptSurePatient(pateintChart, true);
  }
}



// WEBPACK FOOTER //
// ./src/components/eprescribe/eprescribe.component.ts